
import { useState, useEffect, useMemo, useLayoutEffect } from 'react';
import { gsap } from 'gsap';
import Translatable from '../translatable_text/Translatable';

/**
 * Header component with optimized background loading
 * 
 * Optimizations implemented:
 * 1. Preloads background image using Image() constructor for instant display
 * 2. Uses CSS background-image instead of data-background attribute
 * 3. Provides fallback gradient background while loading
 * 4. Shows subtle loading indicator during image load
 * 5. Handles loading errors gracefully
 */

function Header() {
  const [backgroundLoaded, setBackgroundLoaded] = useState(false);
  
  // Memoize the background URL to prevent unnecessary re-renders
  const backgroundImageUrl = useMemo(() => '/light/assets/imgs/header/bg1.webp', []);

  // Preload the background image with error handling
  useEffect(() => {
    const img = new Image();
    
    const handleLoad = () => {
      setBackgroundLoaded(true);
    };
    
    const handleError = () => {
      console.warn('Failed to load background image:', backgroundImageUrl);
      // Still set as loaded to show fallback
      setBackgroundLoaded(true);
    };
    
    img.onload = handleLoad;
    img.onerror = handleError;
    img.src = backgroundImageUrl;
    
    // Cleanup
    return () => {
      img.onload = null;
      img.onerror = null;
    };
  }, [backgroundImageUrl]);

  useLayoutEffect(() => {
    // Only start animation when background is loaded to prevent stagger
    if (!backgroundLoaded) return;
    
    const tl = gsap.timeline();
    tl.fromTo('.header', { y: 50 }, { y: 0 }, '+=0.1');
    tl.fromTo(
      '.header .tw-container',
      { opacity: 0, translateY: 40 },
      { opacity: 1, translateY: 0 },
      '-=0'
    );

    // Cleanup function
    return () => {
      tl.kill();
    }
  }, [backgroundLoaded]);

  // Smooth background transition when image loads
  useEffect(() => {
    if (backgroundLoaded) {
      gsap.to('.header', {
        opacity: 1,
        duration: 0.2,
        ease: 'power2.out'
      });
    }
  }, [backgroundLoaded]);
  
  return (
    <>
      <style>
        {`
          @keyframes spin {
            0% { transform: rotate(0deg); }
            100% { transform: rotate(360deg); }
          }
        `}
      </style>
      <div
        className="header main-header bg-img min-h-[50rem]"
        style={{
          backgroundImage: backgroundLoaded ? `url(${backgroundImageUrl})` : 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
          backgroundSize: 'cover',
          backgroundRepeat: 'no-repeat',
          backgroundPosition: 'center',
          // Start with opacity 0 and initial position for smooth GSAP animation
          opacity: 0,
          transform: 'translateY(200px)'
        }}
        data-overlay-dark="8"
      >
      {/* Subtle loading overlay */}
      {!backgroundLoaded && (
        <div 
          style={{
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: 'rgba(0,0,0,0.1)',
            zIndex: 1,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            backdropFilter: 'blur(1px)'
          }}
        >
          <div 
            style={{
              width: '30px',
              height: '30px',
              border: '2px solid rgba(255,255,255,0.2)',
              borderTop: '2px solid rgba(255,255,255,0.8)',
              borderRadius: '50%',
              animation: 'spin 1s linear infinite'
            }}
          />
        </div>
      )}
      
      <div className='generals' style={{ position: 'relative', zIndex: 2 }}>
        <div style={{paddingTop: '5rem', paddingBottom: '7rem'}} className="tw-container container mx-auto px-4">
          <div className="flex justify-end">
            <div className="w-full lg:w-1/2">
              <div className="text">
                <h4 style={{ fontWeight: 200 }}>
                  <Translatable>
                    A tech startup delivering leading products through an exclusive business lap
                  </Translatable>
                </h4>
              </div>
            </div>
          </div>
        </div>
        <div className="w-full ontop">
          <div className="tw-container container mx-auto px-4">
            <div className="lg-text">
              <div className="flex">
                <h2 className="hidden lg:block text-[1.5rem] pb-5">
                  <Translatable>
                    BUILIDNG DIGITAL INFRASTRUCTURE
                  </Translatable>
                </h2>
              </div>
              <div>

                <h1 className='ltr:hidden'>
                  <Translatable>
                    مختبر
                  </Translatable>
                  <br />
                    جكاد التقني
                </h1>
                  <h1 className='rtl:hidden'>
                  <Translatable>
                    gcad
                  </Translatable>
                  <br />
                    tech lab
                </h1>
              </div>
            </div>
          </div>
          <div className="img">
            <img 
              src="/light/assets/imgs/wide-bg/wide-bg77.webp" 
              alt="img"
              width={900}
              height={500} 
              loading="eager"
              style={{ maxHeight: 500, objectFit: 'cover' }}
            />
          </div>
        </div>
      </div>
    </div>
    </>
  );
}

export default Header;
